import React from 'react';
import { Link } from 'react-router-dom';

import { Callout, Step } from './shared';

const Settings: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Settings</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      Open <strong>Settings</strong> from the sidebar to control how MFlow notifies you, and to manage your account
      data. It has four sections: <strong>Notifications</strong>, <strong>AI agents</strong>,{' '}
      <strong>Automation</strong>, and <strong>Danger zone</strong>.
    </p>

    <h3 className="font-semibold mb-3">Notification preferences</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Sync failures, the daily risk digest, the weekly summary, and proactive insights are all routed through the same
      preferences — you set them once here.
    </p>
    <div className="space-y-3 mb-6">
      <Step n={1}>
        Use the top switch to turn notifications on or off entirely. When off, nothing is sent on any channel.
      </Step>
      <Step n={2}>
        Choose which reports you want. <strong>Daily Standalone Project Digest</strong> is a daily summary of your
        Standalone projects. It&apos;s on by default and goes to the same highest-priority channel as everything else.
      </Step>
      <Step n={3}>
        For each connected channel — Slack, Telegram, Email — toggle it on or off, and set its priority using the
        up/down arrows. Lower number ranks higher priority.
      </Step>
      <Step n={4}>
        Click <strong>Save changes</strong>. The button stays disabled until you&apos;ve actually changed something.
      </Step>
    </div>

    <Callout type="warning">
      Priority is <strong>routing</strong>, not just a display order. Each notification goes to your single
      highest-priority <em>enabled and linked</em> channel — not every channel you have enabled. If that send fails,
      MFlow falls back to the next enabled, linked channel in priority order. A channel you haven&apos;t connected yet
      (see <strong>Connect Slack</strong>, <strong>Connect Telegram</strong>, <strong>Connect Email</strong>) is skipped
      even if it&apos;s enabled here — there&apos;s a &quot;Not linked — connect in Integrations&quot; hint on any
      channel row that needs it.
    </Callout>

    <h3 className="font-semibold mt-6 mb-3">AI agents</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      <strong>Bring your own key</strong> sets the <strong>Default provider</strong> that MFlow&apos;s chat assistants
      use. Choose <em>mFlow-hosted (default)</em> or any provider you&apos;ve saved an API key for in Integrations. This
      doesn&apos;t change the Coding Agent, which uses whatever provider you pick for each task. See{' '}
      <Link to="/docs/ai-providers" className="link link-primary">
        AI Providers &amp; Keys
      </Link>
      .
    </p>

    <h3 className="font-semibold mt-6 mb-3">Automation</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      Two background agents that act on your GitHub repos. Both are <strong>off by default</strong>, and each toggle
      saves as soon as you click it.
    </p>
    <ul className="text-sm text-base-content/70 space-y-2 list-disc list-inside mb-4 leading-relaxed">
      <li>
        <strong>Architecture decision drift check</strong> — once a week, checks your accepted ADRs against the actual
        code and flags any that are no longer followed. See{' '}
        <Link to="/docs/knowledge-base" className="link link-primary">
          Knowledge Base &amp; ADRs
        </Link>
        .
      </li>
      <li>
        <strong>Git Reviewer agent</strong> — every 10 minutes, reviews open pull requests in your connected repos. See{' '}
        <Link to="/docs/git-reviewer" className="link link-primary">
          Git Reviewer
        </Link>
        .
      </li>
    </ul>

    <h3 className="font-semibold mt-6 mb-3">Danger zone</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Permanently delete your synced data, or your data and account together. Click <strong>Delete account</strong> to
      open the confirmation dialog, then choose one of two scopes:
    </p>
    <ul className="text-sm text-base-content/70 space-y-1 list-disc list-inside mb-4">
      <li>
        <strong>Delete data</strong> — removes every project, sync history, and chat session, but keeps your login so
        you can start fresh.
      </li>
      <li>
        <strong>Delete data &amp; account</strong> — check &quot;Also delete my account&quot; first. You will not be
        able to sign in with this account again.
      </li>
    </ul>

    <Callout type="warning">This action is permanent and cannot be undone.</Callout>
  </div>
);

export default Settings;
